import type { DriveItem, Site, Drive } from '@microsoft/microsoft-graph-types';
import { ResponseType } from '@microsoft/microsoft-graph-client';
import { getGraphClient } from './graphClient';
import {
  withRetry,
  READ_POLICY,
  WRITE_POLICY,
  DeltaPageSchema,
  DriveItemSchema,
  MeSchema,
  MemberOfResponseSchema,
  parseGraph,
} from '@repancy/graph-core';
import { enqueue as enqueuePendingWrite } from '../edits/localEditQueue';

export interface DeltaPage {
  value: DriveItem[];
  nextLink?: string;
  deltaLink?: string;
}

/** GET /me — the signed-in user's profile. Used by /trust/permissions. */
export async function getMe(): Promise<{
  id: string;
  displayName?: string;
  userPrincipalName?: string;
  mail?: string;
  jobTitle?: string;
  officeLocation?: string;
}> {
  return withRetry(READ_POLICY, async () => {
    const client = getGraphClient();
    const res = await client.api('/me').get();
    return parseGraph(MeSchema, res, '/me');
  });
}

/** GET /me/memberOf — Entra ID groups + directory roles for the signed-in user. */
export async function getMyGroups(top = 20): Promise<{
  value: Array<{ id: string; displayName?: string; '@odata.type'?: string }>;
}> {
  return withRetry(READ_POLICY, async () => {
    const client = getGraphClient();
    const res = await client.api('/me/memberOf').top(top).get();
    return parseGraph(MemberOfResponseSchema, res, '/me/memberOf');
  });
}

/**
 * Search SharePoint sites the user can see.
 *
 * Per Microsoft docs (https://learn.microsoft.com/en-us/graph/api/site-search):
 *   - Delegated work/school apps can ONLY use /sites?search={query}.
 *     /sites/getAllSites and bare /sites are Application permissions only.
 *   - Graph rejects "*", empty string, and " " as invalid search expressions.
 *
 * Empty query => returns sites matching a generic discovery term (the SPA
 * shows them in the picker tree). Caller passes a real term for actual search.
 */
export async function listSites(query?: string): Promise<Site[]> {
  return withRetry(READ_POLICY, async () => {
    const client = getGraphClient();
    const term = query && query.trim().length > 0 ? query.trim() : 'sharepoint';
    const res = await client.api(`/sites?search=${encodeURIComponent(term)}`).get();
    return (res.value ?? []) as Site[];
  });
}

/**
 * Direct lookup of a single site by its Graph `${hostname},${siteGuid},${webGuid}` id.
 * Used by the portal path so newly-provisioned sites resolve even before
 * SharePoint Search has indexed them (Search lag is minutes-to-hours).
 * Returns null on 404 so callers can fall back to listSites().
 */
export async function getSiteById(siteId: string): Promise<Site | null> {
  return withRetry(READ_POLICY, async () => {
    const client = getGraphClient();
    try {
      const res = await client.api(`/sites/${siteId}`).get();
      return res as Site;
    } catch (err) {
      const status = (err as { statusCode?: number; status?: number })
        .statusCode
        ?? (err as { statusCode?: number; status?: number }).status;
      if (status === 404) return null;
      throw err;
    }
  });
}

/** Drives (document libraries) under a SharePoint site. */
export async function listDrives(siteId: string): Promise<Drive[]> {
  return withRetry(READ_POLICY, async () => {
    const client = getGraphClient();
    const res = await client.api(`/sites/${siteId}/drives`).get();
    return (res.value ?? []) as Drive[];
  });
}

/**
 * Children of an item. Pass itemId='root' to list the drive root.
 * Returns folders and files mixed; callers filter by `folder` vs `file` facet.
 */
export async function listChildren(driveId: string, itemId: string = 'root'): Promise<DriveItem[]> {
  return withRetry(READ_POLICY, async () => {
    const client = getGraphClient();
    const path = itemId === 'root'
      ? `/drives/${driveId}/root/children`
      : `/drives/${driveId}/items/${itemId}/children`;
    const res = await client.api(path).get();
    return (res.value ?? []) as DriveItem[];
  });
}

export async function getItem(driveId: string, itemId: string): Promise<DriveItem> {
  return withRetry(READ_POLICY, async () => {
    const client = getGraphClient();
    return (await client.api(`/drives/${driveId}/items/${itemId}`).get()) as DriveItem;
  });
}

/**
 * Download item content. Returns the raw response so callers can branch on
 * content-type (text vs binary). For preview/import, callers typically pass
 * the body through a text decoder; binary types should be flagged in mapping.
 */
export async function getContent(driveId: string, itemId: string): Promise<ArrayBuffer> {
  return withRetry(READ_POLICY, async () => {
    const client = getGraphClient();
    return (await client
      .api(`/drives/${driveId}/items/${itemId}/content`)
      .responseType(ResponseType.ARRAYBUFFER)
      .get()) as ArrayBuffer;
  });
}

/**
 * Save content back to SharePoint. If `etag` is provided, sends an If-Match
 * header so a concurrent edit produces a 412 / 409 we can surface as a conflict.
 * Returns the new driveItem (with fresh etag).
 *
 * NOTE: 412 / 409 are NOT retryable — they signal a real conflict, not a
 * transient error. withRetry's isRetryableStatus check treats them as
 * fail-fast (4xx other than 408/425/429). saveBack.ts catches them and
 * surfaces a ConflictDialog instead of retrying.
 */
export async function putContent(
  driveId: string,
  itemId: string,
  body: ArrayBuffer | Blob | string,
  etag?: string,
): Promise<DriveItem> {
  return withRetry(
    WRITE_POLICY,
    async () => {
      const client = getGraphClient();
      const req = client.api(`/drives/${driveId}/items/${itemId}/content`);
      if (etag) {
        req.header('If-Match', etag);
      }
      const raw = await req.put(body);
      return parseGraph(DriveItemSchema, raw, 'PUT /drives/{}/items/{}/content') as DriveItem;
    },
    {
      // F1b: on retry exhaust, queue the pending write so it can be replayed
      // when connectivity returns. Conflicts (412/409) are surfaced by
      // saveBack's own catch and don't reach here. F3: parsed via zod to
      // catch Graph contract drift on the returned DriveItem eTag.
      enqueueOnExhaust: async (hint) => {
        // Only queue text/serializable bodies. Binary bodies (Blob/ArrayBuffer)
        // are bypassed because IDB-structured-clone-then-replay risks
        // corruption on long-pending writes (e.g. a 30-minute-old binary that
        // assumes the user's clipboard hasn't changed). String-only for v1.
        if (typeof body !== 'string') return;
        await enqueuePendingWrite({
          kind: 'save-back',
          targetRef: { driveId, itemId },
          payload: { body },
          baseEtag: etag,
        });
        // Lazy-import the toast bus so retryPolicy.ts stays a leaf module
        // and tests of the wrapper don't pull React.
        const { pushGraphErrorToast } = await import('./useGraphErrorToast');
        pushGraphErrorToast(
          new Error(
            `SharePoint write queued for retry (${hint.errorKind === 'transient' ? `HTTP ${hint.lastStatus ?? 'network'}` : hint.errorKind}). Your edit is safe and will sync when Graph is reachable.`,
          ),
        );
      },
    },
  );
}

/**
 * Look up a driveItem by drive-relative path. Returns null on 404 so
 * callers can probe for existence without try/catch around HTTP codes.
 * Path may be empty (== drive root) or any segment relative to root.
 */
export async function getItemByPath(driveId: string, path: string): Promise<DriveItem | null> {
  const cleaned = (path ?? '').replace(/^\/+|\/+$/g, '');
  return withRetry(READ_POLICY, async () => {
    const client = getGraphClient();
    const url = cleaned
      ? `/drives/${driveId}/root:/${encodeGraphPath(cleaned)}:`
      : `/drives/${driveId}/root`;
    try {
      return (await client.api(url).get()) as DriveItem;
    } catch (err) {
      const e = err as { statusCode?: number; code?: string };
      if (e?.statusCode === 404 || e?.code === 'itemNotFound') return null;
      throw err;
    }
  });
}

/**
 * F3 — PUT content to a drive-relative path (Graph upserts: creates if
 * missing, replaces if present). The pending-writes tray uses this for
 * NIPE-proposed new docs after the user approves them. Wrapped with
 * WRITE_POLICY so transient failures retry on the same enqueue path as
 * putContent.
 */
export async function putContentByPath(
  driveId: string,
  folderPath: string,
  fileName: string,
  body: ArrayBuffer | Blob | string,
): Promise<DriveItem> {
  const folder = (folderPath ?? '').replace(/^\/+|\/+$/g, '');
  const childPath = folder ? `${folder}/${fileName}` : fileName;
  return withRetry(
    WRITE_POLICY,
    async () => {
      const client = getGraphClient();
      const url = `/drives/${driveId}/root:/${encodeGraphPath(childPath)}:/content`;
      const raw = await client.api(url).put(body);
      return parseGraph(DriveItemSchema, raw, 'PUT /drives/{}/root:/{}:/content') as DriveItem;
    },
    {
      enqueueOnExhaust: async (hint) => {
        if (typeof body !== 'string') return;
        await enqueuePendingWrite({
          kind: 'save-back',
          targetRef: { driveId, itemId: `root:/${childPath}:` },
          payload: { body },
        });
        const { pushGraphErrorToast } = await import('./useGraphErrorToast');
        pushGraphErrorToast(
          new Error(
            `SharePoint write queued for retry (${hint.errorKind === 'transient' ? `HTTP ${hint.lastStatus ?? 'network'}` : hint.errorKind}). Your edit is safe and will sync when Graph is reachable.`,
          ),
        );
      },
    },
  );
}

/**
 * Encode a path for use in Graph's `/root:/...:` form. Slashes between
 * segments are preserved; each segment is encoded so spaces / unicode
 * land cleanly.
 */
function encodeGraphPath(path: string): string {
  return path
    .split('/')
    .map((seg) => encodeURIComponent(seg))
    .join('/');
}

/**
 * One page of /drives/{id}/root/delta. Callers loop on nextLink until they
 * receive a deltaLink, then persist that deltaLink for next time.
 */
export async function deltaPage(driveId: string, deltaLink?: string): Promise<DeltaPage> {
  return withRetry(READ_POLICY, async () => {
    const client = getGraphClient();
    const path = deltaLink
      ? deltaLink.replace(/^https:\/\/graph\.microsoft\.com\/v1\.0/, '')
      : `/drives/${driveId}/root/delta`;
    const res = await client.api(path).get();
    const parsed = parseGraph(DeltaPageSchema, res, `GET ${path}`);
    return {
      value: parsed.value as DriveItem[],
      nextLink: parsed['@odata.nextLink'],
      deltaLink: parsed['@odata.deltaLink'],
    };
  });
}

/**
 * Drain /delta from the given cursor (or from scratch). Returns the full set of
 * changed items and the final deltaLink to persist for the next run.
 */
export async function drainDelta(driveId: string, deltaLink?: string): Promise<{ items: DriveItem[]; nextDeltaLink: string }> {
  const items: DriveItem[] = [];
  let cursor = deltaLink;
  while (true) {
    const page = await deltaPage(driveId, cursor);
    items.push(...page.value);
    if (page.deltaLink) {
      return { items, nextDeltaLink: page.deltaLink };
    }
    if (!page.nextLink) {
      // Should not happen — delta always terminates with a deltaLink — but fail soft.
      return { items, nextDeltaLink: cursor ?? '' };
    }
    cursor = page.nextLink;
  }
}
